/**
 * EditorialGuidesIndex — public directory of editorial guides.
 *
 * Routes:
 *   /guides              → all published guides across cities
 *   /:city/guides        → all published guides for a city
 *
 * Reuses the existing GET /api/public/guides?city=... endpoint.
 * Layout matches the magazine vibe of the home page so the user feels
 * continuity when they click "See all guides".
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, BookOpen, Eye, MapPin, Newspaper } from 'lucide-react';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const titleize = (s = '') =>
  String(s).replace(/[-_]/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());

export default function EditorialGuidesIndex() {
  const { city } = useParams();
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    const q = city ? `?city=${encodeURIComponent(city)}&limit=200` : '?limit=200';
    axios.get(`${API}/public/guides${q}`)
      .then((r) => { if (live) { setGuides(r.data?.guides || []); setLoading(false); } })
      .catch(() => { if (live) { setGuides([]); setLoading(false); } });
    return () => { live = false; };
  }, [city]);

  const cityLabel = city ? titleize(city) : 'every city';
  const headline = city ? `City guides for ${cityLabel}` : 'Every city guide on Eventism';

  return (
    <div className="min-h-screen bg-background" data-testid="guides-index">
      <SEO
        title={`${headline} · Eventism`}
        description={`Editor-picked, locally curated guides for ${cityLabel}. The best places to drink, eat, and go out — updated weekly.`}
        canonical={city ? `/${city}/guides` : '/guides'}
      />

      <section className="pt-20 pb-10 sm:pt-28 sm:pb-14 border-b border-border/50 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,rgba(217,70,239,0.14),transparent_55%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold text-muted-foreground hover:text-foreground mb-6"
            data-testid="guides-back-home"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to home
          </Link>
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-4">
            <Newspaper className="h-3.5 w-3.5 text-amber-300" />
            <span>The Eventism Edition</span>
            <span className="text-border">·</span>
            <span>{guides.length} {guides.length === 1 ? 'guide' : 'guides'}</span>
          </div>
          <h1
            className="font-bold font-['Space_Grotesk'] tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-6xl"
            data-testid="guides-index-h1"
          >
            {city ? (
              <>
                <span className="bg-gradient-to-r from-amber-300 via-fuchsia-300 to-indigo-300 bg-clip-text text-transparent">
                  {cityLabel}
                </span>{' '}
                guides
              </>
            ) : (
              <>
                Every city,{' '}
                <span className="bg-gradient-to-r from-amber-300 via-fuchsia-300 to-indigo-300 bg-clip-text text-transparent">
                  edited.
                </span>
              </>
            )}
          </h1>
          <p className="mt-4 text-base text-muted-foreground max-w-2xl leading-relaxed">
            Editor-picked spots, ranked by what locals actually book.
            Updated weekly, curated by humans, verified against live availability.
          </p>
        </div>
      </section>

      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="guides-loading">
              {Array.from({ length: 6 }).map((_, i) => (
                <Card key={i} className="border-border overflow-hidden">
                  <div className="aspect-[5/3] bg-muted/40 animate-pulse" />
                  <CardContent className="p-4 space-y-2">
                    <div className="h-4 bg-muted/40 rounded animate-pulse w-2/3" />
                    <div className="h-3 bg-muted/40 rounded animate-pulse w-1/2" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : guides.length === 0 ? (
            <Card className="border-dashed border-2 border-border" data-testid="guides-empty">
              <CardContent className="p-12 text-center space-y-3">
                <BookOpen className="h-10 w-10 mx-auto text-fuchsia-300" />
                <p className="text-lg font-bold font-['Space_Grotesk']">
                  No guides published for {cityLabel} yet.
                </p>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  Our editors are still scouting. Check back next week — or
                  pick another city from the home page.
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-1 text-sm font-bold text-fuchsia-300 hover:text-fuchsia-200"
                >
                  Back to home →
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="guides-grid">
              {guides.map((g) => (
                <Link
                  key={`${g.city}-${g.slug}`}
                  to={`/${g.city}/guides/${g.slug}`}
                  className="group block"
                  data-testid={`guide-card-${g.slug}`}
                >
                  <Card className="border-border hover:border-amber-400/60 transition-all h-full overflow-hidden hover:translate-y-[-2px]">
                    <div className="aspect-[5/3] bg-card overflow-hidden relative">
                      {g.cover_image ? (
                        <img
                          src={g.cover_image}
                          alt={g.title}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-amber-500/20 via-fuchsia-500/15 to-indigo-500/20 flex items-center justify-center">
                          <BookOpen className="h-10 w-10 text-amber-300/70" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                      <Badge className="absolute top-3 left-3 bg-fuchsia-500 hover:bg-fuchsia-500 text-white border-0 text-[10px] uppercase tracking-widest font-bold">
                        City guide
                      </Badge>
                      {g.category_label && (
                        <Badge
                          variant="outline"
                          className="absolute top-3 right-3 bg-black/40 backdrop-blur border-white/30 text-white text-[10px] uppercase tracking-widest font-bold"
                        >
                          {g.category_label}
                        </Badge>
                      )}
                    </div>
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                        <MapPin className="h-3 w-3 text-fuchsia-300" />
                        {titleize(g.city)}
                        {g.view_count != null && (
                          <>
                            <span className="text-border">·</span>
                            <Eye className="h-3 w-3" />
                            {g.view_count} reads
                          </>
                        )}
                      </div>
                      <h3 className="font-bold font-['Space_Grotesk'] text-lg leading-tight line-clamp-2 group-hover:text-amber-200 transition-colors">
                        {g.title}
                      </h3>
                      {g.meta_description && (
                        <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                          {g.meta_description}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
